import { authFetch } from '../../api/csrf';
import React, { useState, useEffect } from 'react';
import {
    X, Send, Paperclip, FileImage, XCircle, Mail, User, Type, AtSign,
    ChevronDown, PenTool, Link2, Smile, Box, Image as ImageIcon,
    Lock, Edit3, Calendar, MoreVertical, Trash2
} from 'lucide-react';
import Button from '../common/Button/Button';
import RichTextEditor from '../common/RichTextEditor/RichTextEditor';
import CustomConfirm from '../common/CustomConfirm';
import { toast } from 'react-hot-toast';
import communicationLogService from '../../api/services/communicationLogService';

const SendEmailModal = ({
    isOpen,
    onClose,
    customerEmail,
    customerId,
    customerName,
    customerProfileImage,
    isBulk = false,
    bulkRecipientIds = [],
    onSent
}) => {
    const [toInput, setToInput] = useState('');
    const [recipients, setRecipients] = useState([]);
    const [ccInput, setCcInput] = useState('');
    const [ccRecipients, setCcRecipients] = useState([]);
    const [showCc, setShowCc] = useState(false);
    const [subject, setSubject] = useState('');
    const [content, setContent] = useState('');
    const [attachments, setAttachments] = useState([]);
    const [isSending, setIsSending] = useState(false);
    const [showToolbar, setShowToolbar] = useState(true);
    const [uploadProgress, setUploadProgress] = useState(null);
    const [previewImage, setPreviewImage] = useState(null);
    const [showCancelConfirm, setShowCancelConfirm] = useState(false);

    // Initialize with main customer when modal opens
    useEffect(() => {
        if (isOpen && !isBulk && customerEmail && !recipients.some(r => r.email === customerEmail)) {
            setRecipients([{
                name: customerName || customerEmail.split('@')[0],
                email: customerEmail,
                avatar: customerProfileImage
            }]);
        } else if (isOpen && isBulk && bulkRecipientIds.length > 0) {
            // In bulk mode, we just show a placeholder or count
            setRecipients([{
                name: `Bulk Selection (${bulkRecipientIds.length} recipients)`,
                email: 'multiple-recipients@bulk',
                avatar: null,
                isPlaceholder: true
            }]);
        }
    }, [isOpen, customerEmail, customerName, customerProfileImage, isBulk, bulkRecipientIds]);

    const removeRecipient = (index) => {
        setRecipients(prev => prev.filter((_, i) => i !== index));
    };

    const removeCcRecipient = (index) => {
        setCcRecipients(prev => prev.filter((_, i) => i !== index));
    };

    const handleFileChange = (e) => {
        const files = Array.from(e.target.files);

        if (attachments.length + files.length > 5) {
            toast.error('You can only upload a maximum of 5 files.');
            e.target.value = null;
            return;
        }

        const validFiles = files.filter(file => {
            const isTypeValid = file.type === 'application/pdf' || file.type.startsWith('image/');
            const isSizeValid = file.size <= 25 * 1024 * 1024; // 25 MB limit

            if (!isTypeValid) toast.error(`File ${file.name} is not a PDF or Image.`);
            if (!isSizeValid) toast.error(`File ${file.name} exceeds 25MB limit.`);

            return isTypeValid && isSizeValid;
        });

        // Generate temporary IDs for immediate UI feedback
        const newAttachments = validFiles.map(file => ({
            id: Math.random().toString(36).substr(2, 9),
            name: file.name,
            size: file.size,
            type: file.type,
            uploading: true,
            url: null
        }));

        setAttachments(prev => [...prev, ...newAttachments]);
        e.target.value = null;

        // Upload each file immediately
        validFiles.forEach(async (file, index) => {
            const tempId = newAttachments[index].id;
            try {
                const res = await communicationLogService.uploadAttachment(file);
                if (res && res.success) {
                    setAttachments(prev => prev.map(att =>
                        att.id === tempId ? {
                            ...att,
                            uploading: false,
                            url: res.data.url,
                            publicId: res.data.public_id,
                            resourceType: res.data.resource_type
                        } : att
                    ));
                } else {
                    toast.error(`Upload failed for ${file.name}`);
                    setAttachments(prev => prev.filter(att => att.id !== tempId));
                }
            } catch (error) {
                toast.error(`Error uploading ${file.name}`);
                setAttachments(prev => prev.filter(att => att.id !== tempId));
            }
        });
    };

    const removeAttachment = async (index) => {
        const fileToRemove = attachments[index];

        // Optimistically remove from UI
        setAttachments(prev => prev.filter((_, i) => i !== index));

        // Delete from Cloudinary if already uploaded
        if (fileToRemove && !fileToRemove.uploading && fileToRemove.publicId) {
            try {
                await communicationLogService.deleteAttachment(fileToRemove.publicId, fileToRemove.resourceType);
            } catch (error) {
                // Don't show toast for cleanup failure to keep UI smooth unless critical
            }
        }
    };

    const ensureFilenameHasExtension = (filename, mimeType) => {
        const clean = String(filename || 'document').trim();
        if (/\.[a-zA-Z0-9]+$/.test(clean)) return clean;

        const type = String(mimeType || '').toLowerCase();
        if (type === 'application/pdf') return `${clean}.pdf`;
        if (type === 'image/jpeg' || type === 'image/jpg') return `${clean}.jpg`;
        if (type === 'image/png') return `${clean}.png`;
        return clean;
    };

    const downloadFile = async (file) => {
        if (file.uploading || !file.url) return;

        const resolvedName = ensureFilenameHasExtension(file.name, file.type);

        try {
            // High-fidelity download using Blob to force naming
            const response = await authFetch(file.url);
            if (!response.ok) throw new Error('Fetch failed');
            const blob = await response.blob();
            const blobUrl = URL.createObjectURL(blob);

            const link = document.createElement('a');
            link.href = blobUrl;
            link.setAttribute('download', resolvedName);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
        } catch (error) {
            // Fallback for CORS restricted URLs (usually doesn't happen with Cloudinary but safe to have)
            let downloadUrl = file.url;
            if (downloadUrl.includes('res.cloudinary.com')) {
                const fileNameWithoutExt = resolvedName.split('.')[0];
                downloadUrl = downloadUrl.replace('/upload/', `/upload/fl_attachment:${fileNameWithoutExt}/`);
            }
            window.open(downloadUrl, '_blank');
        }
    };

    const handleToKeyDown = (e) => {
        if (e.key === 'Enter' || e.key === ',' || e.key === ' ') {
            if (e.key !== 'Enter') e.preventDefault();
            const val = toInput.trim().replace(/,$/, '');
            if (val && val.includes('@')) {
                setRecipients(prev => [...prev, { name: val.split('@')[0], email: val }]);
                setToInput('');
            }
        } else if (e.key === 'Backspace' && !toInput && recipients.length > 0) {
            removeRecipient(recipients.length - 1);
        }
    };

    const handleCcKeyDown = (e) => {
        if (e.key === 'Enter' || e.key === ',' || e.key === ' ') {
            if (e.key !== 'Enter') e.preventDefault();
            const val = ccInput.trim().replace(/,$/, '');
            if (val && val.includes('@')) {
                setCcRecipients(prev => [...prev, { name: val.split('@')[0], email: val }]);
                setCcInput('');
            }
        } else if (e.key === 'Backspace' && !ccInput && ccRecipients.length > 0) {
            removeCcRecipient(ccRecipients.length - 1);
        }
    };

    const handleSend = async () => {
        const allRecipients = recipients.map(r => r.email).join(', ');
        const finalTo = toInput ? (allRecipients ? `${allRecipients}, ${toInput}` : toInput) : allRecipients;

        const allCc = ccRecipients.map(r => r.email).join(', ');
        const finalCc = ccInput ? (allCc ? `${allCc}, ${ccInput}` : ccInput) : allCc;

        const plainText = content.replace(/<[^>]*>/g, '').trim();

        if (!finalTo || !subject || !content || content === '<p><br></p>') {
            toast.error('Please fill in all required fields (To, Subject, Message)');
            return;
        }

        if (plainText.length > 2000) {
            toast.error(`Message is too long. Please limit to 2000 characters (Current: ${plainText.length})`);
            return;
        }

        if (attachments.some(att => att.uploading)) {
            toast.error('Please wait for files to finish uploading');
            return;
        }

        setIsSending(true);
        try {
            setUploadProgress('Sending email...');

            const uploadedAttachments = attachments.map(att => ({
                name: att.name,
                url: att.url,
                size: att.size,
                type: att.type
            }));

            // 2. Prepare payload
            if (isBulk) {
                const emailData = {
                    recipientIds: bulkRecipientIds,
                    cc: finalCc,
                    subject: subject,
                    message: content,
                    communicationType: 'email',
                    category: 'crm',
                    priority: 'medium',
                    attachments: uploadedAttachments
                };

                await communicationLogService.sendBulkEmail(emailData);
                toast.success(`Successfully queued bulk email for ${bulkRecipientIds.length} recipients`);
            } else {
                const emailData = {
                    recipient: finalTo,
                    cc: finalCc,
                    subject: subject,
                    message: content,
                    customerId: customerId,
                    communicationType: 'email',
                    category: 'crm',
                    priority: 'medium',
                    attachments: uploadedAttachments
                };

                await communicationLogService.createCommunicationLog(emailData);
                if (onSent) {
                    await onSent(emailData);
                }
                toast.success('Email sent successfully');
            }

            setTimeout(() => {
                onClose();
                setCcRecipients([]);
                setCcInput('');
                setShowCc(false);
                setSubject('');
                setContent('');
                setAttachments([]);
                setRecipients([]);
                setToInput('');
                setShowToolbar(false);
                setUploadProgress(null);
            }, 1000);

        } catch (error) {
            const errorMsg = error.response?.data?.responseMessage || error.message || 'Failed to send email';
            toast.error(errorMsg);
            setUploadProgress(null);
        } finally {
            setIsSending(false);
        }
    };

    const handleConfirmCancel = () => {
        setContent('');
        setSubject('');
        setAttachments([]);
        onClose();
        setShowCancelConfirm(false);
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[9999] p-4 animate-in fade-in duration-300">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[95vh] flex flex-col overflow-hidden border border-gray-100 transform transition-all scale-100">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-3.5 border-b bg-gray-50/30">
                    <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center shadow-sm">
                            <Mail className="h-4 w-4 text-blue-600" />
                        </div>
                        <h2 className="font-bold text-gray-800">New Message</h2>
                    </div>
                    <button onClick={onClose} className="text-gray-400 hover:text-red-500 transition-colors p-1 hover:bg-gray-100 rounded-lg">
                        <X className="h-5 w-5" />
                    </button>
                </div>

                {/* Body */}
                <div className="flex-1 overflow-y-auto custom-scrollbar flex flex-col">
                    {/* Email Header Section (Gmail Style) */}
                    <div className="px-6 py-1 space-y-0 border-b">
                        {/* To Row */}
                        <div className="flex items-start border-b border-gray-100 py-3 group">
                            <span className="text-gray-500 w-8 text-sm font-medium mt-1">To</span>
                            <div className="flex flex-wrap flex-1 gap-1.5 min-h-[32px] items-center">
                                {recipients.map((rec, index) => (
                                    <div
                                        key={index}
                                        className="flex items-center gap-1.5 bg-white border border-gray-300 rounded-full pl-0.5 pr-1.5 py-0.5 shadow-sm hover:border-blue-400 transition-all cursor-default group/chip"
                                    >
                                        <div className="w-6 h-6 rounded-full overflow-hidden flex-shrink-0 border border-gray-100">
                                            {rec.avatar ? (
                                                <img src={rec.avatar} alt={rec.name} className="w-full h-full object-cover" />
                                            ) : (
                                                <div className="w-full h-full bg-agrosaf-green text-[10px] text-white flex items-center justify-center font-bold">
                                                    {rec.name.charAt(0).toUpperCase()}
                                                </div>
                                            )}
                                        </div>
                                        <span className="text-xs font-semibold text-gray-700">{rec.name}</span>
                                        <button
                                            onClick={() => removeRecipient(index)}
                                            className="text-gray-400 hover:text-red-500 rounded-full hover:bg-red-50 p-0.5"
                                        >
                                            <X className="h-3 w-3" />
                                        </button>
                                    </div>
                                ))}
                                <input
                                    value={toInput}
                                    onChange={(e) => setToInput(e.target.value)}
                                    onKeyDown={handleToKeyDown}
                                    className="flex-1 outline-none text-sm bg-transparent min-w-[120px]"
                                    placeholder={recipients.length === 0 ? "Recipients (comma or space separated)" : ""}
                                />
                            </div>
                            {!showCc && (
                                <button
                                    onClick={() => setShowCc(true)}
                                    className="text-gray-400 hover:text-gray-900 text-sm font-medium cursor-pointer ml-2 mt-1"
                                >
                                    Cc
                                </button>
                            )}
                        </div>

                        {/* CC Row */}
                        {showCc && (
                            <div className="flex items-start border-b border-gray-100 py-3 animate-in slide-in-from-top-1 duration-200">
                                <span className="text-gray-500 w-8 text-sm font-medium mt-1">Cc</span>
                                <div className="flex flex-wrap flex-1 gap-1.5 min-h-[32px] items-center">
                                    {ccRecipients.map((rec, index) => (
                                        <div
                                            key={index}
                                            className="flex items-center gap-1.5 bg-white border border-gray-300 rounded-full pl-0.5 pr-1.5 py-0.5 shadow-sm hover:border-blue-400 transition-all cursor-default group/chip"
                                        >
                                            <div className="w-6 h-6 rounded-full overflow-hidden flex-shrink-0 border border-gray-100">
                                                <div className="w-full h-full bg-blue-500 text-[10px] text-white flex items-center justify-center font-bold">
                                                    {rec.name.charAt(0).toUpperCase()}
                                                </div>
                                            </div>
                                            <span className="text-xs font-semibold text-gray-700">{rec.name}</span>
                                            <button
                                                onClick={() => removeCcRecipient(index)}
                                                className="text-gray-400 hover:text-red-500 rounded-full hover:bg-red-50 p-0.5"
                                            >
                                                <X className="h-3 w-3" />
                                            </button>
                                        </div>
                                    ))}
                                    <input
                                        value={ccInput}
                                        onChange={(e) => setCcInput(e.target.value)}
                                        onKeyDown={handleCcKeyDown}
                                        className="flex-1 outline-none text-sm bg-transparent min-w-[120px]"
                                        placeholder="CC recipients"
                                        autoFocus
                                    />
                                </div>
                            </div>
                        )}

                        {/* Subject Row */}
                        <div className="flex items-center py-3">
                            <input
                                placeholder="Subject"
                                value={subject}
                                onChange={(e) => setSubject(e.target.value)}
                                className="flex-1 outline-none text-sm font-medium bg-transparent"
                            />
                        </div>
                    </div>

                    {/* Custom Toolbar - Positioned above editor for better connectivity */}
                    <div
                        id="email-modal-toolbar"
                        className={`px-6 py-2 border-b bg-[#f8fafc] flex items-center transition-all duration-300 ${showToolbar ? 'block' : 'hidden'}`}
                    >
                        <span className="ql-formats">
                            <select className="ql-header" defaultValue="">
                                <option value="1">Heading</option>
                                <option value="2">Bold Heading</option>
                                <option value="">Normal</option>
                            </select>
                        </span>
                        <div className="w-[1px] h-6 bg-gray-200 mx-1"></div>
                        <span className="ql-formats">
                            <button className="ql-bold"></button>
                            <button className="ql-italic"></button>
                            <button className="ql-underline"></button>
                            <button className="ql-strike"></button>
                        </span>
                        <div className="w-[1px] h-6 bg-gray-200 mx-1"></div>
                        <span className="ql-formats">
                            <select className="ql-color"></select>
                            <select className="ql-background"></select>
                        </span>
                        <div className="w-[1px] h-6 bg-gray-200 mx-1"></div>
                        <span className="ql-formats">
                            <button className="ql-list" value="ordered"></button>
                            <button className="ql-list" value="bullet"></button>
                        </span>
                        <span className="ql-formats">
                            <button className="ql-indent" value="-1"></button>
                            <button className="ql-indent" value="+1"></button>
                        </span>
                        <span className="ql-formats">
                            <select className="ql-align"></select>
                        </span>
                        <div className="w-[1px] h-6 bg-gray-200 mx-1"></div>
                        <span className="ql-formats">
                            <button className="ql-link"></button>
                            <button className="ql-clean"></button>
                        </span>
                    </div>

                    <div className="flex-1 px-6 pt-4 flex flex-col min-h-[500px]">
                        <RichTextEditor
                            value={content}
                            onChange={setContent}
                            height="450px"
                            placeholder="Write your email message here..."
                            className="!mb-0 flex-1 border-none shadow-none email-editor"
                            toolbarId="email-modal-toolbar"
                        />
                    </div>

                    {/* Attachments Section */}
                    <div className="px-6 py-4 border-t bg-gray-50/50">
                        <div className="flex items-center justify-between">
                            <div className="flex flex-col">
                                <label className="text-sm font-semibold text-gray-700">Attachments</label>
                                <span className="text-[11px] font-medium text-gray-400 uppercase tracking-wider">PDF and Images only (Max 25MB)</span>
                            </div>
                            <div className="flex flex-col items-end">
                                <span className={`text-[11px] font-bold uppercase tracking-wider ${content.replace(/<[^>]*>/g, '').length > 2000 ? 'text-red-500 animate-pulse' : 'text-gray-400'}`}>
                                    Characters: {content.replace(/<[^>]*>/g, '').length}/2000
                                </span>
                            </div>
                        </div>

                        <div className="flex items-start gap-4 flex-wrap mt-4">
                            <label className="flex flex-col items-center justify-center w-32 h-32 border-2 border-dashed border-gray-200 rounded-2xl cursor-pointer hover:border-agrosaf-green hover:bg-green-50/30 transition-all group">
                                <div className="flex flex-col items-center justify-center pt-5 pb-6">
                                    <Paperclip className="w-8 h-8 text-gray-300 group-hover:text-agrosaf-green transition-colors mb-2" />
                                    <p className="text-xs font-semibold text-gray-400 group-hover:text-agrosaf-green">Add Files</p>
                                </div>
                                <input type="file" multiple onChange={handleFileChange} className="hidden" accept="application/pdf,image/*" />
                            </label>

                            {attachments.map((file, index) => (
                                <div
                                    key={index}
                                    onClick={() => downloadFile(file)}
                                    className="group relative w-32 h-32 bg-gray-50 rounded-2xl border border-gray-100 flex flex-col items-center justify-center p-3 text-center overflow-hidden transition-all duration-300 cursor-pointer hover:shadow-md"
                                    title={file.uploading ? "Uploading..." : `Click to download ${file.name}`}
                                >
                                    {file.uploading ? (
                                        <div className="flex flex-col items-center justify-center gap-2">
                                            <div className="animate-spin h-8 w-8 border-2 border-blue-500 border-t-transparent rounded-full" />
                                            <div className="text-[10px] font-bold text-blue-500 animate-pulse tracking-wide uppercase">Uploading...</div>
                                        </div>
                                    ) : (
                                        <>
                                            {file.type.startsWith('image/') ? (
                                                <div className="absolute inset-0 z-0 scale-100 group-hover:scale-110 transition-transform duration-500">
                                                    <img src={file.url} alt={file.name} className="w-full h-full object-cover" />
                                                    <div className="absolute inset-0 bg-black/10 transition-opacity group-hover:opacity-0" />
                                                </div>
                                            ) : (
                                                <div className="w-12 h-12 bg-red-50 rounded-xl flex items-center justify-center mb-2 shadow-sm">
                                                    <Paperclip className="h-6 w-6 text-red-500" />
                                                </div>
                                            )}

                                            <div className="relative z-10 w-full mt-auto bg-white/90 backdrop-blur-sm p-1.5 rounded-xl border border-white/50 shadow-sm">
                                                <p className="text-[10px] font-bold text-gray-800 truncate px-1">{file.name}</p>
                                                <p className="text-[8px] font-semibold text-gray-400">{(file.size / 1024).toFixed(0)} KB</p>
                                            </div>
                                        </>
                                    )}

                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            removeAttachment(index);
                                        }}
                                        className="absolute top-2 right-2 p-1.5 bg-white shadow-md rounded-full text-red-500 opacity-0 group-hover:opacity-100 transition-all hover:bg-red-50 z-20 border border-gray-100 active:scale-95"
                                        title="Remove file"
                                    >
                                        <XCircle className="h-4 w-4" />
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>


                <div className="px-6 py-4 border-t flex items-center justify-end gap-3 bg-white">
                    {/* Cancel Button */}
                    <button
                        onClick={() => setShowCancelConfirm(true)}
                        className="px-6 py-2.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-xl font-semibold text-sm transition-colors"
                        title="Cancel and discard draft"
                    >
                        Cancel
                    </button>

                    {/* Send Button */}
                    <button
                        onClick={handleSend}
                        disabled={isSending}
                        className="bg-[#0b57d0] hover:bg-[#0842a0] text-white px-8 py-2.5 rounded-full font-semibold text-sm transition-colors flex items-center gap-2 disabled:opacity-50 shadow-sm"
                    >
                        {isSending ? (uploadProgress || 'Sending...') : 'Send'}
                    </button>
                </div>

                {/* Custom Confirm Dialog */}
                <CustomConfirm
                    isOpen={showCancelConfirm}
                    title="Confirm Action"
                    message={
                        <>
                            Are you sure you want to cancel?<br />
                            Canceling will discard your message
                        </>
                    }
                    confirmText="Yes"
                    cancelText="No"
                    onConfirm={handleConfirmCancel}
                    onCancel={() => setShowCancelConfirm(false)}
                />
            </div>

            <style jsx global>{`
          .custom-scrollbar::-webkit-scrollbar {
            width: 6px;
          }
          .custom-scrollbar::-webkit-scrollbar-track {
            background: transparent;
          }
          .custom-scrollbar::-webkit-scrollbar-thumb {
            background: #e5e7eb;
            border-radius: 10px;
          }
          .email-editor .ql-container {
            border: none !important;
          }
          .email-editor .ql-editor {
            font-size: 14px;
            color: #222;
            min-height: 400px;
          }
          .email-editor .ql-placeholder {
            color: #999;
            font-style: normal;
          }
          #email-modal-toolbar {
            border-top: 1px solid #eee;
            display: flex;
            flex-direction: row;
            flex-wrap: wrap;
            padding: 4px 6px;
          }
          #email-modal-toolbar .ql-header {
            width: 120px !important;
            padding: 2px 4px !important;
            height: 30px !important;
            font-size: 13px !important;
            border-radius: 4px;
            border: 1px solid #e2e8f0;
            background: white;
            cursor: pointer;
          }
          #email-modal-toolbar .ql-formats {
            margin-right: 8px !important;
            display: flex;
            align-items: center;
          }
          #email-modal-toolbar button {
            width: 28px !important;
            height: 28px !important;
            padding: 4px !important;
            border-radius: 4px;
            transition: all 0.2s;
            display: flex !important;
            align-items: center !important;
            justify-content: center !important;
            text-indent: 0 !important;
          }
          #email-modal-toolbar button:hover {
            background-color: #f1f5f9;
          }
          #email-modal-toolbar .ql-active {
            color: #0b57d0 !important;
            background-color: #e8f0fe !important;
          }
          #email-modal-toolbar .ql-picker-label {
            padding: 2px 4px !important;
            display: flex !important;
            align-items: center !important;
            justify-content: center !important;
            border: none !important;
            outline: none !important;
          }
          #email-modal-toolbar .ql-picker {
            margin: 0 4px !important;
            height: 28px !important;
          }
          #email-modal-toolbar .ql-header.ql-picker {
            width: 100px !important;
          }
          #email-modal-toolbar .ql-color.ql-picker .ql-picker-label svg,
          #email-modal-toolbar .ql-background.ql-picker .ql-picker-label svg,
          #email-modal-toolbar .ql-align.ql-picker .ql-picker-label svg {
            width: 18px !important;
            height: 18px !important;
          }
          #email-modal-toolbar .ql-stroke {
            stroke: #444 !important;
          }
          #email-modal-toolbar .ql-fill {
            fill: #444 !important;
          }
          #email-modal-toolbar .ql-picker-options {
            background-color: #fff !important;
            border: 1px solid #ddd !important;
            box-shadow: 0 2px 8px rgba(0,0,0,0.1) !important;
            border-radius: 4px !important;
          }
        `}</style>
        </div >
    );
};

export default SendEmailModal;
